import * as React from 'react';
import _ from 'lodash'
declare const I18n;
import DOMPurify from "dompurify/dist/purify.min";
import formatTime from '../../../utils/formatTime';

type Props = {
  cardBackground: "primary" | "secondary"
  title: string,
  description: string,
  targetRole: string,
  index: number,
  id: number,
  image?: string,
  isFacilitator?: boolean,
  isLearner?: boolean,
  handleDelete(id: number): void,
  type?: string,
  duration?: string,
  category?: string,
  slug: string,
  time?: string,
  date?: string,
  location?: string,
  categories?: Array<{ id: number, name: string }>,
  start_time?: string,
  end_time?: string,
  user_id?: number,
  current_user_id?: number,
}

const CardItem: React.FunctionComponent<Props> = ({
  cardBackground = "primary",
  title,
  description,
  targetRole,
  index,
  id,
  handleDelete,
  image,
  isFacilitator,
  isLearner,
  type = 'learn more',
  slug,
  date,
  location,
  categories,
  start_time,
  end_time,
  user_id,
  current_user_id
}: Props): JSX.Element => {
  const mod = index % 3;
  let levelText = `${_.capitalize(targetRole)}`;

  const getCategoryColor = (categoryId: number): string => {
    const colorMap: { [key: number]: string } = {
      1: '#42C0C6',
      2: '#E70880',
      3: '#8DC862',
      4: '#42C0C6',
      5: '#8DC862',
      6: '#42C0C6',
      7: '#E70880',
      8: '#8DC862',
      9: '#E70880',
    };

    return colorMap[categoryId] || '#97A165';
  };

  const DOMPurifyConfig = {
    ADD_TAGS: ["iframe"],
    ADD_ATTR: ["target", "allow", "allowfullscreen", "frameborder", "scrolling", "src", "width", "height"],
  };

  const isAdmin = !(isLearner || isFacilitator);

  // Check if facilitator can edit this event (they own it)
  const canEdit = isAdmin || (isFacilitator && user_id === current_user_id);

  // Determine base URL for links
  const baseUrl = isAdmin ? '/admin' : '';

  const imageNode = (
    <div className={`card-list-item__image-container`}>
      {
        image ? (
          <div className="card-list-item__image card-list-item__image--background" style={{ backgroundImage: `url(${image})` }} />
        ) : (
          <div className="empty-resource-container">
          </div>
        )
      }
    </div>
  );

  const cardType = (
    <div className="type">
      <i className={'fa-solid fa-file icon'}></i>
      {I18n.t('models.event.view_event')}
    </div>
  );

  const getTZAbbr = (dateStr?: string) => {
    const date = dateStr ? new Date(dateStr) : new Date();

    return new Intl.DateTimeFormat('en-US', {
      timeZoneName: 'short'
    })
      .formatToParts(date)
      .find(part => part.type === 'timeZoneName')?.value || '';
  };

  const tzAbbr = getTZAbbr(date);

  return (
    <div
      className={`card-list-item ${cardBackground}`}
      style={{
        marginRight: (mod === 0 || mod === 1) ? '1.2%' : 0,
        marginLeft: (mod === 1 || mod === 2) ? '1.2%' : 0
      }}
    >
      <a
        style={{ cursor: 'pointer', textDecoration: 'none', borderRadius: '20px' }}
        href={`${baseUrl}/events/${slug}`}
      >
        {imageNode}
      </a>
      <div className="card-list-item__meta-container">
        <div>
          <div className="subinformation subinformation-column">
            {date && date !== 'null' && date.trim() !== '' && (
              <div className="flex-row">
                <div className="duration">
                  <i className="far fa-calendar-days icon"></i>
                  {date}
                </div>
              </div>
            )}

            {/* Time */}
            {(start_time || end_time) && (
              <div className="flex-row">
                <div className="event_details__labels">
                  <i className="fa-solid fa-clock icon"></i>
                  {start_time && formatTime(start_time)}
                  {start_time && end_time && ' - '}
                  {end_time && formatTime(end_time)}
                  {` (${tzAbbr})`}
                </div>
              </div>
            )}

            {/* Location */}
            {location && (
              <div className="flex-row">
                <div className="event_details__labels">
                  <i className="fa-solid fa-location-dot icon"></i>
                  {location}
                </div>
              </div>
            )}
          </div>
          <div className="card-list-item__title-container">
            {/* Sub information */}
            <div id={`resource_category_title_${id}`} className="card-list-item__title">{title}</div>
            {canEdit && (
              <div className="card-list-item__admin-container">
                <a href={`${baseUrl}/events/${slug}/edit`} aria-label={I18n.t('views.common.edit')}>
                  <i className="fal fa-edit card-list-item__icon--admin" />
                </a>
                <button
                  type="button"
                  id={`delete_${id}`}
                  className="btn btn-link p-0 m-0 align-baseline"
                  onClick={(e) => { e.preventDefault(); if (confirm(I18n.t('views.common.confirm_delete'))) handleDelete(id); }}
                  aria-label={I18n.t('views.common.delete')}
                >
                  <i className="fal fa-trash card-list-item__icon--admin" />
                </button>
              </div>
            )}
          </div>
          <div className="card-list-item__desc-container">
            <div
              id={`event_description_${id}`}
              className="card-list-item__description"
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(description.substring(0, 80), DOMPurifyConfig),
              }}
            />
          </div>
        </div>
        <div className="flex-start">
          {/* Categories */}
          {categories && categories.length > 0 && (
            <div className="flex-row">
              <div className="event_details__labels">
                {categories.map((cat) => (
                  <span
                    key={cat.id}
                    className="categories"
                    style={{ backgroundColor: getCategoryColor(cat.id) }}
                  >
                    {cat.name}
                  </span>
                ))}
              </div>
            </div>
          )}
          <a
            style={{ cursor: 'pointer', textDecoration: 'none' }}
            href={`${baseUrl}/events/${slug}`}
            className={'btn btn-sm btn-primary btn-resource'}
          >
            {type && cardType}
          </a>
        </div>
      </div>
    </div>
  );
}

export default CardItem;